import "@workos-inc/widgets/styles.css";

import { UsersManagement, WorkOsWidgets } from "@workos-inc/widgets";
import { Loader } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { VerificationTypes } from "@/components/custom-verifications-table";
import { ApiKeysSection } from "@/components/organization/api-keys-section";
import { ConsumerAuthSection } from "@/components/organization/consumer-auth-section";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRequestAuditLogsMutation } from "@/data/mutations";
import {
  useDownloadAuditLogsQuery,
  useGetCustomVerificationTypesQuery,
  useGetOrganizationQuery,
  useGetWidgetTokenQuery,
} from "@/data/queries";
import { WorkOSPermission } from "@/data/types";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@/lib/env";
import { canManageApiKeys, canManageUsersInWidget } from "@/lib/permissions";
import { WORKOS_WIDGETS_FONT_FAMILY } from "@/lib/styles";
import { convertTimeFromSeconds } from "@/lib/utils";
import useStore from "@/state";

export const Organization = () => {
  const [exportId, setExportId] = useState<string>();
  const { data, isLoading } = useGetOrganizationQuery();
  const requestAuditLogsMutation = useRequestAuditLogsMutation();
  const { data: exportData } = useDownloadAuditLogsQuery(exportId);

  const { data: verificationTypes } = useGetCustomVerificationTypesQuery();

  const { session } = useStore();

  const manageUsersInWidget = useMemo(() => {
    return canManageUsersInWidget(session?.permissions);
  }, [session?.permissions]);

  const manageApiKeys = useMemo(() => {
    return canManageApiKeys(session?.permissions);
  }, [session?.permissions]);

  const { data: widgetAuth } = useGetWidgetTokenQuery([
    WorkOSPermission.WIDGET_USER_TABLE_MANAGE,
  ]);

  const handleRequestLogs = async () => {
    const auditLogExport = await requestAuditLogsMutation.mutateAsync();
    setExportId(auditLogExport.id);
  };

  if (isLoading) {
    return <div />;
  }

  if (!data) {
    return <div>Organization not found</div>;
  }

  return (
    <div>
      <h2 className="scroll-m-20 border-b pb-2 text-3xl font-medium tracking-tight first:mt-0">
        Organization
      </h2>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Details
      </h3>

      <Card className="p-6 mt-6">
        <p className="text-sm text-muted-foreground mb-1">Name</p>
        <p className="font-medium">{data.workos_org.name}</p>

        <p className="text-sm text-muted-foreground mt-6 mb-1">
          Organization ID
        </p>
        <p className="font-medium">
          {data.organization.workos_organization_id}
        </p>
      </Card>

      <ConsumerAuthSection loginOptions={data.organization.login_options} />

      {manageApiKeys && <ApiKeysSection />}

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Usage
      </h3>

      <div className="mt-6 grid grid-cols-4 gap-6">
        <Card className="p-6">
          <p>Total SKUs</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.templates_count}
          </p>
        </Card>

        <Card className="p-6">
          <p>Total Tags</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.tags_count}
          </p>
        </Card>

        <Card className="p-6">
          <p>Total Tag Verifications</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.tag_verifications_count}
          </p>
        </Card>

        <Card className="p-6">
          <p>API Tag Verifications</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.external_tag_verifications_count}
          </p>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-4 gap-6">
        <Card className="p-6">
          <p>Page Views</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.tag_view_verifications_count}
          </p>
        </Card>

        <Card className="p-6">
          <p>Marketing Consent</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.email_consent_tags_count}
          </p>
        </Card>

        <Card className="p-6">
          <p>Ownership Claims</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.claim_count}
          </p>
        </Card>

        <Card className="p-6">
          <p>Ownership Resets</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.reset_count}
          </p>
        </Card>
      </div>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Assets
      </h3>

      <div className="mt-6 grid grid-cols-4 gap-6">
        <Card className="p-6">
          <p>Images</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.images_count}
          </p>
        </Card>

        <Card className="p-6">
          <p>Videos</p>
          <p className="text-2xl font-medium mt-6">
            {data.organization.videos_count}
          </p>
        </Card>

        <Card className="p-6">
          <p>Video Duration</p>
          <p className="text-2xl font-medium mt-6">
            {convertTimeFromSeconds(
              data.organization.videos_duration_seconds_count,
            )}
          </p>
        </Card>

        <Card className="p-6">
          <p>Music and Other Files</p>
          <p className="text-2xl font-medium mt-6">--</p>
        </Card>
      </div>

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Users
      </h3>

      {!manageUsersInWidget && (
        <Card className="mt-6 p-6">
          <div className="space-y-2">
            {data.workos_org_users.map((user) => (
              <div className="flex gap-2 items-center">
                <Avatar className="rounded-lg">
                  <AvatarImage src={user.profile_picture_url ?? undefined} />
                  <AvatarFallback className="rounded-lg">
                    {user?.first_name?.[0]}
                    {user?.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs">
                    {user.email} ({user.id})
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {manageUsersInWidget && widgetAuth && (
        <Card className="mt-6 p-6">
          <WorkOsWidgets
            className="!min-h-min"
            theme={{ fontFamily: WORKOS_WIDGETS_FONT_FAMILY }}
          >
            <UsersManagement authToken={widgetAuth?.token} />
          </WorkOsWidgets>
        </Card>
      )}

      <VerificationTypes verificationTypes={verificationTypes} />

      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Audit Log
      </h3>

      <p className="mt-4">
        Request to download a 30-day history of all activities in your
        organization.
      </p>

      <div className="flex gap-2 mt-6">
        <Button
          variant="secondary"
          onClick={handleRequestLogs}
          disabled={requestAuditLogsMutation.isPending || !!exportId}
        >
          {requestAuditLogsMutation.isPending ? (
            <Loader className="h-3 w-3 animate-spin" />
          ) : null}
          Request Audit Logs
        </Button>
        <Link to={exportData?.url ?? ""} target="_blank" download>
          <Button disabled={!exportData?.url}>Download Audit Logs</Button>
        </Link>
      </div>

      <p className="mt-12 text-sm">
        View our{" "}
        <a
          href={TERMS_OF_SERVICE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Terms of Service
        </a>{" "}
        and{" "}
        <a
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
};
